/**
 * ActivityList Component
 * Feature: Activities - Real data from Supabase
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CheckCircle2, Circle, Loader2, Building2, Calendar, ChevronDown, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useActivities, useActivityStats } from "../hooks";
import type { Attivita } from "@/types";

// Status mapping from database to UI
const STATUS_MAP: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  "da_fare":    { label: "Da fare",    icon: <Circle className="h-4 w-4 text-amber-500" />, color: "text-amber-700", bg: "bg-amber-50" },
  "in_corso":   { label: "In corso",   icon: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />, color: "text-blue-700", bg: "bg-blue-50" },
  "completata": { label: "Completata", icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />, color: "text-emerald-700", bg: "bg-emerald-50" },
  "annullata":  { label: "Annullata",  icon: <AlertCircle className="h-4 w-4 text-gray-400" />, color: "text-gray-600", bg: "bg-gray-100" },
};

const STATUS_OPTIONS = ["Tutti", "da_fare", "in_corso", "completata", "annullata"] as const;
const PRIORITY_COLORS: Record<string, string> = {
  "bassa": "bg-gray-400",
  "normale": "bg-blue-500",
  "alta": "bg-amber-500",
  "urgente": "bg-red-500",
};

interface ActivityListProps {
  onAddActivity?: () => void;
}

export function ActivityList({ onAddActivity }: ActivityListProps) {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<string>("Tutti");
  
  const { data: activities = [], isLoading } = useActivities();
  const { data: stats } = useActivityStats();

  // Filter activities
  const filteredActivities = activities.filter(a =>
    filterStatus === "Tutti" || a.stato === filterStatus
  );

  // Group by date
  const grouped: Record<string, Attivita[]> = {};
  filteredActivities.forEach((a) => {
    const date = a.data_scadenza || "Senza scadenza";
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(a);
  });
  const dates = Object.keys(grouped).sort();

  const handleAddActivity = () => {
    if (onAddActivity) {
      onAddActivity();
    } else {
      navigate("/operativo/attivita/nuovo");
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-32 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
        <Skeleton className="h-10 w-40" />
        <div className="space-y-3">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Attività</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{activities.length} attività in programma</p>
        </div>
        <button 
          onClick={handleAddActivity} 
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" /> Nuova attività
        </button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Da fare",    count: stats?.daFare || 0,     color: "bg-amber-50 border-amber-200 text-amber-700" },
          { label: "In corso",   count: stats?.inCorso || 0,    color: "bg-blue-50 border-blue-200 text-blue-700" },
          { label: "Completate", count: stats?.completate || 0, color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
        ].map(s => (
          <div key={s.label} className={cn("rounded-xl border p-4", s.color)}>
            <div className="text-2xl font-bold">{s.count}</div>
            <div className="text-xs font-medium mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <select 
            value={filterStatus} 
            onChange={e => setFilterStatus(e.target.value)}
            className="appearance-none border border-border rounded-xl pl-3.5 pr-8 py-2 text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === "Tutti" ? "Tutti" : STATUS_MAP[s]?.label || s}</option>)}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* List grouped by date */}
      {filteredActivities.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-16 text-center">
          <CheckCircle2 className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">Nessuna attività trovata</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredActivities.map(activity => {
            const status = STATUS_MAP[activity.stato] || STATUS_MAP["da_fare"];
            const priorityColor = PRIORITY_COLORS[activity.priorita] || "bg-gray-400";
            
            return (
              <div 
                key={activity.id} 
                className="bg-card border border-border rounded-xl p-4 flex items-start gap-3 hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => navigate(`/operativo/attivita/${activity.id}`)}
              >
                <div className={cn("w-1 self-stretch rounded-full flex-shrink-0", priorityColor)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className={cn("text-xs font-medium px-2.5 py-0.5 rounded-full", status.bg, status.color)}>
                      {status.label}
                    </span>
                    {activity.data_scadenza && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(activity.data_scadenza).toLocaleDateString("it-IT")}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-medium text-foreground mb-1">
                    {activity.titolo}
                  </div>
                  {activity.descrizione && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{activity.descrizione}</p>
                  )}
                </div>
                <div className="flex-shrink-0">{status.icon}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
